"use client";

import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { focusFirstErrorAfterRender } from "@/components/form";
import { SectionHeading } from "@/components/ui";
import { useSaveDebt } from "@/features/refinance/hooks/use-save-debt";
import { useUnfinishedDebts } from "@/features/refinance/hooks/use-unfinished-debts";
import { RefinanceDebt } from "@/features/refinance/types";
import type { LockedDebt } from "@/features/refinance/utils/lea-bank-debts";
import { AddDebtButton } from "./add-debt-button";
import { DebtCard } from "./debt-card";
import { EmptyDebtsCard } from "./empty-debts-card";
import { LockedDebtCard } from "./locked-debt-card";

export interface DebtListItem {
	id: string;
	index: number;
	removable: boolean;
	seed?: RefinanceDebt;
}

interface DebtListProps {
	items: DebtListItem[];
	lockedDebts?: LockedDebt[];
	heading?: string;
	customerAccountNumber?: string | null;
	onAdd: () => void;
	onRemove: (index: number) => void;
}

export const DebtList = ({
	items,
	lockedDebts = [],
	heading,
	customerAccountNumber,
	onAdd,
	onRemove,
}: DebtListProps) => {
	const t = useTranslations("pages.RefinancePage");
	const unfinished = useUnfinishedDebts(items);
	const saveDebt = useSaveDebt(customerAccountNumber);
	const [expandIndex, setExpandIndex] = useState<number | null>(null);
	const [revealAttempt, setRevealAttempt] = useState(0);
	const listRef = useRef<HTMLDivElement>(null);

	// A half-finished debt is saved first, or shows what it still needs.
	const handleAdd = () => {
		const saved = unfinished.map(saveDebt);
		if (saved.includes(false)) {
			setRevealAttempt((attempt) => attempt + 1);
			focusFirstErrorAfterRender(listRef);
			return;
		}

		setExpandIndex(items.length);
		onAdd();
	};

	return (
		<div ref={listRef} className="flex min-w-0 flex-col gap-2">
			{heading && <SectionHeading>{heading}</SectionHeading>}

			{lockedDebts.map((debt) => (
				<LockedDebtCard key={debt.id} {...debt} />
			))}

			{items.map(({ id, index, removable, seed }) => (
				<DebtCard
					key={id}
					index={index}
					seed={seed}
					removable={removable}
					defaultExpanded={index === expandIndex}
					revealAttempt={revealAttempt}
					customerAccountNumber={customerAccountNumber}
					onRemove={() => onRemove(index)}
				/>
			))}

			{items.length > 0 ? (
				<AddDebtButton
					title={t("debtList.addAnotherButton")}
					onClick={handleAdd}
				/>
			) : (
				<EmptyDebtsCard onAdd={handleAdd} />
			)}
		</div>
	);
};
